import {
  Box,
  Flex,
  HStack,
  Stack,
  Button,
  useDisclosure,
  useToast,
  Tag,
  Avatar,
  IconButton,
  Divider,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaEdit, FaShare, FaTrash, FaPlug, FaCamera, FaUndo, FaPlus } from 'react-icons/fa';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanLoading } from '../HelivanLoading';
import { EditAgentModal } from './EditAgentModal';
import { ShareAgentModal } from './ShareAgentModal';
import { CreateNewProjectModal } from '../Projects/CreateNewProjectModal';
import { AgentMonitoringPanel } from './AgentMonitoringPanel';
import { GatewayHealthTag } from './GatewayHealthTag';
import {
  useGetCustomAgentQuery,
  useDeleteCustomAgentMutation,
  useRevertAgentToSnapshotMutation,
  useRefreshAgentSnapshotMutation,
} from '../../generated/graphql';
import { useConnectionTest } from '../../hooks/useConnectionTest';

export const AgentDetailsView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const editModal = useDisclosure();
  const shareModal = useDisclosure();
  const createProjectModal = useDisclosure();

  const { data, loading, refetch } = useGetCustomAgentQuery({
    variables: { id: id! },
    skip: !id,
  });

  const [deleteAgent] = useDeleteCustomAgentMutation();
  const [revertToSnapshot, { loading: revertLoading }] = useRevertAgentToSnapshotMutation();
  const [refreshSnapshot, { loading: refreshLoading }] = useRefreshAgentSnapshotMutation();
  const { test: testConnection, loading: testLoading, result: testResult } = useConnectionTest();

  const agent = data?.getCustomAgent;

  if (loading) {
    return <HelivanLoading />;
  }

  if (!agent) {
    return (
      <Flex
        width="full"
        height="full"
        alignItems="center"
        justifyContent="center"
      >
        <Stack spacing={4} textAlign="center">
          <HelivanText variant="sectionHeader">Agent Not Found</HelivanText>
          <Button onClick={() => navigate('/agents')}>
            Back to My Agents
          </Button>
        </Stack>
      </Flex>
    );
  }

  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this agent?')) return;
    try {
      await deleteAgent({
        variables: { id: agent.id },
      });
      toast({
        title: 'Agent deleted',
        status: 'success',
        duration: 3000,
      });
      navigate('/agents');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete agent',
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Stack width="full" height="full" spacing={0} overflow="hidden">
      <EditAgentModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        agent={agent}
        onSuccess={() => refetch()}
      />
      <ShareAgentModal
        isOpen={shareModal.isOpen}
        onClose={shareModal.onClose}
        agent={agent}
        onSuccess={() => refetch()}
      />
      <CreateNewProjectModal
        isOpen={createProjectModal.isOpen}
        onClose={createProjectModal.onClose}
        hasTokens={true}
        preSelectedCustomAgentId={agent.id}
        preSelectedCustomAgentName={agent.name}
      />

      {/* Header */}
      <Flex
        width="full"
        alignItems="center"
        py="12px"
        px="24px"
        bg={HelivanColor.background.primary}
        borderBottom="1px solid"
        borderBottomColor={HelivanColor.border.primary}
        justifyContent="space-between"
        gap={4}
        flexShrink={0}
      >
        <HStack spacing={4}>
          <IconButton
            aria-label="Back"
            icon={<FaArrowLeft />}
            variant="ghost"
            onClick={() => navigate('/agents')}
          />
          <HelivanText variant="sectionHeader">{agent.name}</HelivanText>
          <Tag colorScheme={
            (agent.connectionType === 'CONNECTOR'
              ? agent.connectorStatus === 'ONLINE'
              : agent.status === 'ACTIVE')
              ? 'green' : 'gray'
          }>
            {(agent.connectionType === 'CONNECTOR'
              ? agent.connectorStatus === 'ONLINE'
              : agent.status === 'ACTIVE')
              ? 'Online' : 'Offline'}
          </Tag>
          {agent.connectionType === 'CONNECTOR' && agent.connectorStatus === 'ONLINE' && (
            <GatewayHealthTag
              connectionType={agent.connectionType}
              gatewayHealth={agent.gatewayHealth}
              gatewayHealthReason={agent.gatewayHealthReason}
            />
          )}
          {agent.lastConnectionTestAt && (
            <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
              Tested {formatRelativeTime(agent.lastConnectionTestAt)}
            </HelivanText>
          )}
        </HStack>

        <HStack spacing={3}>
          <Button
            leftIcon={<FaPlus />}
            colorScheme="blue"
            size="sm"
            onClick={createProjectModal.onOpen}
          >
            Create watch
          </Button>
          {agent.canEdit && (
            <Button
              leftIcon={<FaEdit />}
              variant="outline"
              size="sm"
              onClick={editModal.onOpen}
            >
              Edit
            </Button>
          )}
          {agent.isOwner && (
            <>
              <Button
                leftIcon={<FaShare />}
                variant="outline"
                size="sm"
                onClick={shareModal.onOpen}
              >
                Share
              </Button>
              <Button
                leftIcon={<FaTrash />}
                colorScheme="red"
                variant="outline"
                size="sm"
                onClick={handleDelete}
              >
                Delete
              </Button>
            </>
          )}
        </HStack>
      </Flex>

      {/* Content */}
      <Flex
        flex="1"
        overflowY="auto"
        bg={HelivanColor.background.secondary}
        p={6}
        gap={6}
      >
        {/* Left column - Agent info */}
        <Box
          flex="1"
          bg={HelivanColor.background.primary}
          borderRadius="12px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          p={6}
        >
          <Stack spacing={6}>
            {/* Agent header */}
            <Flex gap={4} alignItems="flex-start">
              <Avatar
                size="xl"
                name={agent.name}
                src={agent.avatarUrl ?? undefined}
                bg={HelivanColor.text.helivanBlue}
                color="white"
              />
              <Stack spacing={2}>
                <HelivanText variant="sectionHeader">{agent.name}</HelivanText>
                {agent.description && (
                  <HelivanText
                    variant="largeBody"
                    colorOverride={HelivanColor.text.blueMid}
                  >
                    {agent.description}
                  </HelivanText>
                )}
                {agent.owner && !agent.isOwner && (
                  <HelivanText
                    variant="smallBody"
                    colorOverride={HelivanColor.text.blueMid}
                  >
                    Created by {agent.owner.fullName}
                  </HelivanText>
                )}
              </Stack>
            </Flex>

            <Divider />

            {/* Configuration details */}
            <Stack spacing={4}>
              <HelivanText variant="largeSubheader">Configuration</HelivanText>

              <Box
                p={4}
                bg={HelivanColor.background.secondary}
                borderRadius="8px"
              >
                <Stack spacing={3}>
                  {agent.connectionType === 'CONNECTOR' ? (
                    <>
                      <Flex justify="space-between" align="center">
                        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                          Connection Type
                        </HelivanText>
                        <Tag colorScheme="purple" size="sm">Connector</Tag>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                          Connector Status
                        </HelivanText>
                        <HStack spacing={2}>
                          <Tag
                            colorScheme={agent.connectorStatus === 'ONLINE' ? 'green' : 'gray'}
                            size="sm"
                          >
                            {agent.connectorStatus === 'ONLINE' ? 'Online' : 'Offline'}
                          </Tag>
                          <Button
                            size="xs"
                            leftIcon={<FaPlug />}
                            colorScheme={
                              testResult.status === 'success'
                                ? 'green'
                                : testResult.status === 'error'
                                ? 'red'
                                : 'blue'
                            }
                            variant="outline"
                            isLoading={testLoading}
                            onClick={() => testConnection(agent.id)}
                          >
                            Test
                          </Button>
                        </HStack>
                      </Flex>
                      <Flex justify="space-between" align="center">
                        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                          Gateway Health
                        </HelivanText>
                        <GatewayHealthTag
                          connectionType={agent.connectionType}
                          gatewayHealth={agent.gatewayHealth}
                          gatewayHealthReason={agent.gatewayHealthReason}
                        />
                      </Flex>
                      {agent.lastConnectorPingAt && (
                        <Flex justify="space-between">
                          <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                            Last Ping
                          </HelivanText>
                          <HelivanText variant="smallBody">
                            {formatRelativeTime(agent.lastConnectorPingAt)}
                          </HelivanText>
                        </Flex>
                      )}
                    </>
                  ) : (
                    <Flex justify="space-between" align="center">
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                        API Endpoint
                      </HelivanText>
                      <HStack spacing={2}>
                        <HelivanText variant="smallBody">{agent.apiEndpoint}</HelivanText>
                        <Button
                          size="xs"
                          leftIcon={<FaPlug />}
                          colorScheme={
                            testResult.status === 'success'
                              ? 'green'
                              : testResult.status === 'error'
                              ? 'red'
                              : 'blue'
                          }
                          variant="outline"
                          isLoading={testLoading}
                          onClick={() => testConnection(agent.id)}
                        >
                          Test
                        </Button>
                      </HStack>
                    </Flex>
                  )}
                  {testResult.status !== 'idle' && (
                    <HelivanText
                      variant="smallBody"
                      colorOverride={
                        testResult.status === 'success'
                          ? 'green.500'
                          : 'red.500'
                      }
                    >
                      {testResult.message}
                    </HelivanText>
                  )}
                  <Flex justify="space-between">
                    <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                      Auth Type
                    </HelivanText>
                    <HelivanText variant="smallBody">{agent.apiAuthType}</HelivanText>
                  </Flex>
                  <Flex justify="space-between">
                    <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                      Temperature
                    </HelivanText>
                    <HelivanText variant="smallBody">{agent.temperature ?? 0.7}</HelivanText>
                  </Flex>
                  <Flex justify="space-between">
                    <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                      Max Tokens
                    </HelivanText>
                    <HelivanText variant="smallBody">{agent.maxTokens ?? 1000}</HelivanText>
                  </Flex>
                </Stack>
              </Box>

              {agent.systemPrompt && (
                <Box>
                  <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                    System Prompt
                  </HelivanText>
                  <Box
                    mt={2}
                    p={3}
                    bg={HelivanColor.background.secondary}
                    borderRadius="8px"
                    maxHeight="200px"
                    overflowY="auto"
                  >
                    <HelivanText variant="smallBody" style={{ whiteSpace: 'pre-wrap' }}>
                      {agent.systemPrompt}
                    </HelivanText>
                  </Box>
                </Box>
              )}

              {/* Snapshot Status */}
              {agent.snapshotEnabled && (
                <Box
                  p={4}
                  bg={HelivanColor.background.secondary}
                  borderRadius="8px"
                >
                  <Flex justify="space-between" align="center" mb={2}>
                    <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                      State Snapshot
                    </HelivanText>
                    <Tag colorScheme="blue" size="sm">Enabled</Tag>
                  </Flex>
                  {agent.snapshot ? (
                    <>
                      <HelivanText variant="smallBody">
                        Saved {formatRelativeTime(agent.snapshot.createdAt)}
                      </HelivanText>
                      {agent.isOwner && (
                        <HStack mt={3} spacing={2}>
                          <Button
                            size="xs"
                            leftIcon={<FaUndo />}
                            variant="outline"
                            colorScheme="orange"
                            isLoading={revertLoading}
                            onClick={async () => {
                              if (!window.confirm('Revert agent configuration to the stored snapshot?')) return;
                              try {
                                await revertToSnapshot({ variables: { agentId: agent.id } });
                                toast({ title: 'Reverted to snapshot', status: 'success', duration: 3000 });
                                refetch();
                              } catch (err) {
                                toast({ title: 'Revert failed', description: err instanceof Error ? err.message : 'Unknown error', status: 'error', duration: 5000 });
                              }
                            }}
                          >
                            Revert
                          </Button>
                          <Button
                            size="xs"
                            leftIcon={<FaCamera />}
                            variant="outline"
                            colorScheme="blue"
                            isLoading={refreshLoading}
                            onClick={async () => {
                              try {
                                await refreshSnapshot({ variables: { agentId: agent.id } });
                                toast({ title: 'Snapshot updated', status: 'success', duration: 3000 });
                                refetch();
                              } catch (err) {
                                toast({ title: 'Update failed', description: err instanceof Error ? err.message : 'Unknown error', status: 'error', duration: 5000 });
                              }
                            }}
                          >
                            Update Snapshot
                          </Button>
                        </HStack>
                      )}
                    </>
                  ) : (
                    <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                      Snapshot will be created on next update
                    </HelivanText>
                  )}
                </Box>
              )}
            </Stack>

            <Divider />

            {/* Stats */}
            <Stack spacing={4}>
              <HelivanText variant="largeSubheader">Statistics</HelivanText>
              <Flex gap={6}>
                <Box>
                  <HelivanText variant="sectionHeader">{agent.shareCount}</HelivanText>
                  <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                    Shares
                  </HelivanText>
                </Box>
                <Box>
                  <HelivanText variant="sectionHeader">{agent.projectCount}</HelivanText>
                  <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                    Projects
                  </HelivanText>
                </Box>
              </Flex>
            </Stack>
          </Stack>
        </Box>

        {/* Right column - Monitoring */}
        {(agent.isOwner || agent.canEdit) && (
          <Box width="400px" flexShrink={0}>
            <AgentMonitoringPanel agentId={agent.id} />
          </Box>
        )}
      </Flex>
    </Stack>
  );
};
